import type { Enemy, GameEvent, GameState } from '@gra/engine';
import {
  AdditiveBlending,
  Color,
  CircleGeometry,
  Group,
  Sprite,
  SpriteMaterial,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  OctahedronGeometry,
  PerspectiveCamera,
  PointLight,
  Scene,
  Vector3,
} from 'three';
import { sfx } from '../audio';
import { settings } from '../settings';
import type { Assets } from './assets';
import { CharacterModel, heroModel, type EquipVisual } from './character';
import { DamageNumbers } from './damage-numbers';
import { Debris } from './debris';
import { DynamicLights } from './lights';
import { Environment } from './environment';
import { WeaponTrail } from './trail';
import { LootView, lootHop } from './loot-view';
import { MapFeaturesView } from './map-features';
import { addRim } from './materials';
import { AbilityViews } from './ability-views';
import { BossView } from './boss-view';
import { createProjectile, FxLayer, glow, type ProjectileView } from './fx';
import { Particles } from './particles';
import type { StageScene } from './stage';
import type { Grade } from './stage';
import type { ArmorVisual, BodyLook } from './body';
import { abilityColor, attackAnimation, ENEMY_VISUALS, HERO_MODELS, WORLD_SCALE as S, ZONE_LOOKS } from './visuals';

/** Kamera pod kątem ~42° (zamiast prawie pionowo) — widać sylwetki, twarze i broń, scena ma głębię. */
const CAMERA_OFFSET = new Vector3(0, 12.5, 14);
/** Kolor pulsowania wroga przed atakiem (lont, salwa). */
const PULSE = new Color('#ff5a1a');
/** Cios wroga w bohatera: iskra i liczba obrażeń. */
const HURT_COLOR = '#ff3b30';
const PROJECTILE_HEIGHT = 1.1;
const BURN_TINT = new Color('#a83a0a');
const SLOW_TINT = new Color('#2a6c8a');
const STUN_TINT = new Color('#8a7a1a');
const DEATH_DURATION = 1.6;
/** Natywna prędkość biegu animacji Running_A (jednostki sceny / s) — do synchronizacji kroków. */
/** Wygładzanie wykładnicze niezależne od liczby klatek: im większe `rate`, tym szybciej dogania cel. */
const damp = (rate: number, dt: number) => 1 - Math.exp(-rate * dt);

/** Jak długo bohater patrzy w stronę ataku, zanim wróci do kierunku biegu. */
const AIM_HOLD = 0.35;

// Kryształ doświadczenia: wydłużony, fasetowany, ze świecącym rdzeniem — z góry nie wygląda jak kwadrat.
const gemGeo = new OctahedronGeometry(0.16, 0).scale(0.8, 1.9, 0.8);
const gemMat = new MeshStandardMaterial({
  color: '#9ff3ff',
  emissive: '#2ec8f0',
  emissiveIntensity: 1.6,
  roughness: 0.05,
  metalness: 0.3,
  transparent: true,
  opacity: 0.92,
  flatShading: true,
});
const gemGlowMat = new SpriteMaterial({ map: glow(), color: '#5fdcff', blending: AdditiveBlending, depthWrite: false, opacity: 0.55, toneMapped: false });
const blobGeo = new CircleGeometry(1, 24);
const blobMat = new MeshBasicMaterial({ color: '#000000', transparent: true, opacity: 0.35, depthWrite: false });

interface HpBar {
  bg: Sprite;
  fill: Sprite;
}

const barBgMat = new SpriteMaterial({ color: '#120808', depthTest: false, transparent: true, opacity: 0.8 });
const barFillMat = new SpriteMaterial({ color: '#e0443a', depthTest: false, toneMapped: false });
const barEliteMat = new SpriteMaterial({ color: '#ff9a3c', depthTest: false, toneMapped: false });

interface EnemyView {
  visual: string;
  model: CharacterModel;
  materials: MeshStandardMaterial[];
  bar: HpBar | null;
  /** Stała poświata (np. płonąca czaszka). */
  tint: Color;
  /** Czas do następnego ciosu wręcz (animacja). */
  swingCd: number;
  /** Wypad do przodu przy ciosie (0..1, wygasa). */
  lunge: number;
  /** Czas do momentu, w którym zamach dosięga bohatera (sekundy; 0 = brak zamachu w toku). */
  strike: number;
  /** Zapalony lont / naładowana salwa: pulsowanie poświaty. */
  charging: number;
  /** 1 → 0 po trafieniu: odchylenie do tyłu i spłaszczenie. */
  flinch: number;
}

interface ProjectileSlot {
  view: ProjectileView;
  fx: string;
}

export interface HeroLook {
  visual: string;
  /** Gołe ciało rasy (manekin UAL) — bez niego model klasy KayKit. */
  body?: BodyLook;
  /** Zbroja na ciele: tułów, nogi, dłonie, stopy. */
  armor: ArmorVisual[];
  /** Broń dwuręczna zmienia animacje ataku. */
  twoHanded: boolean;
  equipment: EquipVisual[];
  aura: { color: string | null; strength: number };
}

/** Scena wyprawy: odwzorowuje stan silnika na modele 3D i efekty. Nie zmienia stanu gry. */
export class RunScene implements StageScene {
  readonly scene = new Scene();
  // near 1, nie 0,1: kamera wisi wysoko nad bohaterem, a 10× dokładniejsza głębia to mniej szumu w cieniach otoczenia (AO).
  readonly camera = new PerspectiveCamera(38, 1, 1, 200);
  readonly grade: Grade;
  readonly rim: { color: string; strength: number };
  private readonly env: Environment;
  private readonly hero: CharacterModel;
  private readonly look: HeroLook;
  private readonly sparks: Particles;
  private readonly smoke: Particles;
  private readonly fx: FxLayer;
  private readonly abilityViews: AbilityViews;
  private readonly numbers: DamageNumbers;
  private readonly loot: LootView;
  private readonly debris: Debris;
  private readonly features: MapFeaturesView;
  private readonly bossView: BossView;
  private readonly lights: DynamicLights;
  private readonly trail: WeaponTrail;
  private readonly trailBase = new Vector3();
  private readonly trailTip = new Vector3();
  /** Dynamiczne oddalenie kamery (tłum, boss) i szarpnięcie przy mocnym ciosie. */
  private zoom = 1;
  private readonly kick = new Vector3();
  private readonly enemies = new Map<number, EnemyView>();
  private readonly pool = new Map<string, EnemyView[]>();
  private dying: { view: EnemyView; t: number }[] = [];
  private readonly projectiles = new Map<number, ProjectileSlot>();
  private readonly shots = new Map<number, ProjectileView>();
  private readonly tmpColor = new Color();
  /** Zwolnienie czasu po pokonaniu bossa. */
  private deathSlowmo = 0;
  private readonly gems = new Map<number, Group>();
  private readonly lastHero = new Vector3();
  private readonly tmp = new Vector3();
  private time = 0;
  private dead = false;
  private aimAngle = 0;
  private aimTimer = 0;
  private altSwing = false;
  /** Wygładzona prędkość bohatera (do wyprzedzania kamerą i pochylenia). */
  private readonly heroVel = new Vector3();
  private lean = 0;
  private bank = 0;
  private lastYaw = 0;
  private readonly gemBorn = new Map<number, number>();
  private frameDt = 0;
  /** Obrażenia od kontaktu zebrane od ostatniego pokazanego ciosu i czas bez pokazanego ciosu. */
  private pendingHurt = 0;
  private hurtQuiet = 0;
  /**
   * Wygładzanie między krokami symulacji (stałe 60 Hz): pozycje sprzed ostatniego kroku i ułamek kroku,
   * który już minął. Bez tego na monitorze 120/144 Hz co druga klatka stoi w miejscu i ruch klatkuje.
   */
  private readonly prevPos = new Map<number, { x: number; y: number }>();
  private alpha = 1;
  private stepTimer = 0;
  private stepSide = 1;
  /** Zatrzymanie klatki po mocnym ciosie (sekundy czasu rzeczywistego). */
  private hitstop = 0;
  private lastHitstop = -1;
  private deathAt = -1;

  constructor(
    private readonly assets: Assets,
    private readonly state: GameState,
    look: HeroLook,
  ) {
    const zone = ZONE_LOOKS[state.content.zones[state.zoneId]?.visual ?? ''] ?? ZONE_LOOKS.cmentarz!;
    this.grade = zone.grade;
    this.rim = zone.rim;
    const obstacleCount = state.content.zones[state.zoneId]?.obstacles ?? 0;
    this.env = new Environment(this.scene, zone, { decor: true, assets, obstacles: { seed: state.seed, count: obstacleCount } });
    this.sparks = new Particles(this.scene, 2500, 0.22);
    this.smoke = new Particles(this.scene, 800, 0.6);
    this.fx = new FxLayer(this.scene, this.sparks, this.smoke);
    this.abilityViews = new AbilityViews(this.scene, state, this.sparks, (id) => this.colorOf(id));
    this.numbers = new DamageNumbers(this.scene);
    this.loot = new LootView(this.scene, assets, state, this.fx, this.sparks);
    this.debris = new Debris(this.scene, assets);
    this.features = new MapFeaturesView(this.scene, assets, state, zone, this.sparks, this.smoke);
    this.bossView = new BossView(this.scene, state, this.env);
    this.lights = new DynamicLights(this.scene);
    this.trail = new WeaponTrail(this.scene);
    this.look = look;
    this.hero = heroModel(assets, HERO_MODELS[look.visual]!, true, look.body);
    this.hero.setArmor(assets, look.armor);
    this.hero.setEquipment(assets, look.equipment);
    this.hero.setAura(look.aura.color, look.aura.strength);
    this.hero.setLoop(this.hero.idleLoop);
    this.scene.add(this.hero.root);
    // Światło bohatera: ciepła lampa nad głową — postać i ziemia wokół zawsze odcinają się od tła (ciemna zbroja
    // na ciemnej trawie inaczej ginęła). Bez cieni, żeby nie kosztowało.
    const heroLight = new PointLight('#fff0dc', 6, 7, 1.6);
    heroLight.position.set(0.6, 3.2, 1.4);
    this.hero.root.add(heroLight);
    this.placeCamera(true);
  }

  resize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    const px = height * (settings.highQuality ? Math.min(2, Math.max(window.devicePixelRatio, 1.5)) : Math.min(1.5, window.devicePixelRatio));
    this.sparks.setViewport(px, this.camera.fov);
    this.smoke.setViewport(px, this.camera.fov);
    this.env.setViewport(px, this.camera.fov);
  }

  private world(x: number, y: number, height = 0): Vector3 {
    return this.tmp.set(x * S, height, y * S);
  }

  handleEvents(events: readonly GameEvent[]): void {
    const abilities = this.state.content.abilities;
    for (const e of events) {
      switch (e.type) {
        case 'enemyHit': {
          const hitView = this.enemies.get(e.id);
          if (hitView) {
            hitView.flinch = 1;
            if (e.crit && !hitView.model.busy) hitView.model.playOnce('Hit_A', { timeScale: 1.6 });
          }
          if (e.crit) {
            this.addHitstop(0.045);
            this.lights.flash(this.world(e.x, e.y, 1.2).clone(), this.colorOf(this.lastAbility), 10, 6, 0.18);
          }
          this.fx.impact(this.world(e.x, e.y, 1.1), this.colorOf(this.lastAbility), e.crit);
          this.numbers.show(this.world(e.x, e.y, 2.3), e.amount, e.crit);
          break;
        }
        case 'enemyDied':
          this.fx.death(this.world(e.x, e.y, 0.5));
          this.killEnemy(e.id);
          break;
        case 'slash': {
          const def = abilities[e.abilityId];
          this.fx.slash(this.world(e.x, e.y), e.angle, e.radius * S, e.arc, this.colorOf(e.abilityId), def?.fx === 'cleave');
          this.flourish(e.abilityId, e.x, e.y, e.radius);
          if (def?.fx === 'cleave') {
            this.addHitstop(0.085);
            this.cameraKick(Math.cos(e.angle), Math.sin(e.angle), 0.35);
          }
          break;
        }
        case 'nova': {
          const def = abilities[e.abilityId];
          this.lights.flash(this.world(e.x, e.y, 1).clone(), this.colorOf(e.abilityId), def?.fx === 'flame' ? 30 : 12, e.radius * S * 1.4, 0.4);
          if (def?.fx === 'flame') this.fx.flame(this.world(e.x, e.y), e.radius * S, this.colorOf(e.abilityId));
          else this.fx.spin(this.world(e.x, e.y), e.radius * S, this.colorOf(e.abilityId), this.hero.root);
          this.flourish(e.abilityId, e.x, e.y, e.radius);
          break;
        }
        case 'chain':
          this.fx.lightning(
            e.points.map((p, i) => new Vector3(p.x * S, i === 0 ? 1.3 : 1.1, p.y * S)),
            this.colorOf(e.abilityId),
          );
          this.lights.flash(this.world(e.points[1]?.x ?? 0, e.points[1]?.y ?? 0, 1.5).clone(), this.colorOf(e.abilityId), 18, 8, 0.2);
          break;
        case 'aura':
          this.lastAbility = e.abilityId;
          this.fx.auraPulse(this.hero.root, e.radius * S, this.colorOf(e.abilityId));
          break;
        case 'trapBurst':
          this.lastAbility = e.abilityId;
          this.fx.trapBurst(this.world(e.x, e.y), e.radius * S, this.colorOf(e.abilityId));
          this.lights.flash(this.world(e.x, e.y, 1).clone(), this.colorOf(e.abilityId), 20, 8, 0.3);
          break;
        case 'abilityUsed':
          this.lastAbility = e.abilityId;
          this.heroAttack(e.abilityId, e.targetX, e.targetY);
          break;
        case 'levelUp':
          this.fx.levelUp(this.hero.root);
          this.lights.flash(this.hero.root.position.clone().setY(2), '#ffd27a', 30, 10, 1.2);
          break;
        case 'playerHurt':
          this.pendingHurt += e.amount;
          break;
        case 'enemyWindup':
          this.enemyWindup(e.id, e.x, e.y, e.angle, e.duration);
          break;
        case 'explosion':
          this.fx.explosion(this.world(e.x, e.y), e.radius * S);
          this.lights.flash(this.world(e.x, e.y, 1.2).clone(), '#ff7a30', 40, 12, 0.6);
          break;
        case 'shotHit':
          this.fx.impact(this.world(e.x, e.y, 1), this.state.content.enemies[e.defId]?.color ?? '#ffffff', false);
          break;
        case 'shotBlocked': {
          // Pocisk rozbity o drzewo albo grób: iskry w kolorze pocisku i kłąb pyłu.
          const color = e.defId ? (this.state.content.enemies[e.defId]?.color ?? '#ffffff') : this.colorOf(e.abilityId ?? null);
          this.fx.impact(this.world(e.x, e.y, 1), color, false);
          this.smoke.emit(this.world(e.x, e.y, 0.9), { color: '#9a8f7a', count: 4, speed: 0.8, life: 0.5, up: 0.6, size: 0.5, grow: 0.8, drag: 4 });
          break;
        }
        case 'bossCast':
          this.lights.flash(this.world(e.x, e.y, 2.5).clone(), '#b58cff', 30, 14, 0.5);
          this.fx.flash(this.world(e.x, e.y, 2.5), this.state.content.enemies[this.state.enemies.find((en) => en.id === e.id)?.defId ?? '']?.color ?? '#ffffff', 5, 0.3);
          this.fx.shake = Math.max(this.fx.shake, 0.2);
          break;
        case 'bossSpawned':
          this.fx.shake = Math.max(this.fx.shake, 0.6);
          break;
        case 'bossDefeated':
          this.fx.explosion(this.world(e.x, e.y), 4);
          this.lights.flash(this.world(e.x, e.y, 2).clone(), '#ffd27a', 60, 18, 1.4);
          this.fx.shake = Math.max(this.fx.shake, 0.7);
          this.deathSlowmo = 1.2;
          break;
        case 'bossImpact': {
          // Cios bossa spada na zaznaczony obszar: wybuch w środku kręgu albo na końcu stożka/pasa.
          const reach = e.shape === 'circle' ? 0 : e.shape === 'cone' ? e.radius * 0.6 : e.length;
          const at = this.world(e.x + Math.cos(e.angle) * reach, e.y + Math.sin(e.angle) * reach).clone();
          const size = (e.shape === 'line' ? e.width : e.radius) * S;
          this.fx.explosion(at, size);
          this.lights.flash(at.clone().setY(1.2), '#ff6a3a', 35, 12, 0.5);
          this.fx.shake = Math.max(this.fx.shake, e.hit ? 0.55 : 0.35);
          if (e.hit) this.addHitstop(0.08);
          break;
        }
        case 'bossEnraged':
          this.lights.flash(this.world(e.x, e.y, 2.5).clone(), '#ff2a1a', 70, 20, 1.2);
          this.fx.flash(this.world(e.x, e.y, 2), '#ff3b2f', 7, 0.5);
          this.fx.shake = Math.max(this.fx.shake, 0.8);
          break;
        case 'bossSummon':
          this.fx.explosion(this.world(e.x, e.y), 2.2);
          this.lights.flash(this.world(e.x, e.y, 1.5).clone(), '#9b7bff', 30, 10, 0.6);
          break;
        case 'breakableBroken':
          this.features.onBroken(e.x, e.y);
          sfx.hit(false);
          break;
        case 'chestOpened':
          this.loot.chestOpened(e.x, e.y);
          this.lights.flash(this.world(e.x, e.y, 1).clone(), '#ffcf60', 25, 9, 0.7);
          break;
        case 'gameOver':
          this.dead = true;
          this.deathAt = this.time;
          this.hero.playOnce('Death_A', { hold: true });
          break;
      }
    }
  }

  /**
   * Znaczniki rzeczy poza ekranem (skrzynie, boss): punkt na krawędzi ekranu w ich kierunku.
   * Zwraca tylko to, czego nie widać.
   */
  offscreenMarkers(width: number, height: number): { x: number; y: number; angle: number; kind: 'chest' | 'boss' }[] {
    const out: { x: number; y: number; angle: number; kind: 'chest' | 'boss' }[] = [];
    const v = new Vector3();
    const margin = 40;
    const add = (wx: number, wy: number, kind: 'chest' | 'boss') => {
      v.set(wx * S, 0.5, wy * S).project(this.camera);
      const sx = (v.x * 0.5 + 0.5) * width;
      const sy = (-v.y * 0.5 + 0.5) * height;
      if (v.z < 1 && sx > margin && sx < width - margin && sy > margin && sy < height - margin) return;
      const cx = width / 2;
      const cy = height / 2;
      const dx = sx - cx;
      const dy = sy - cy;
      const k = Math.min((cx - margin) / Math.max(Math.abs(dx), 1e-6), (cy - margin) / Math.max(Math.abs(dy), 1e-6));
      out.push({ x: cx + dx * k, y: cy + dy * k, angle: Math.atan2(dy, dx), kind });
    };
    for (const c of this.state.chests) add(c.x, c.y, 'chest');
    for (const e of this.state.enemies) if (this.state.bossIds.includes(e.id)) add(e.x, e.y, 'boss');
    return out;
  }

  /** Kolor umiejętności zależny od jej rangi u bohatera. */
  private colorOf(abilityId: string | null): string {
    const def = this.state.content.abilities[abilityId ?? ''];
    if (!def) return '#ffffff';
    const rank = this.state.abilities.find((a) => a.id === abilityId)?.rank ?? 1;
    return abilityColor(def, rank, this.state.content.game.abilityMaxRank);
  }

  /**
   * Delikatna oprawa ciosów zależna od rangi: od rangi III kilka iskier na krawędzi,
   * po ewolucji trochę więcej, w kolorze umiejętności. Bez błysków i wstrząsów — ma być ładnie, nie głośno.
   */
  private flourish(abilityId: string, x: number, y: number, radius: number): void {
    const def = this.state.content.abilities[abilityId];
    const rank = this.state.abilities.find((a) => a.id === abilityId)?.rank ?? 1;
    if (!def || (rank < 3 && !def.evolved)) return;
    const color = this.colorOf(abilityId);
    const r = radius * S;
    const count = def.evolved ? 7 : rank - 2;
    const p = new Vector3();
    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const d = r * (0.6 + Math.random() * 0.4);
      p.set(x * S + Math.cos(a) * d, 0.3 + Math.random() * 0.8, y * S + Math.sin(a) * d);
      this.sparks.emit(p, { color, count: 1, speed: 1.6, life: 0.45, up: 1.2, gravity: 2, size: 0.13, hot: true, drag: 1.5 });
    }
  }

  /** Smuga za bronią bohatera w trakcie ataku, w kolorze umiejętności (z rangą). */
  private updateTrail(dt: number): void {
    const has = this.hero.weaponSegment(this.trailBase, this.trailTip);
    const def = this.state.content.abilities[this.lastAbility ?? ''];
    const melee = def?.kind === 'melee' || def?.kind === 'nova';
    if (melee) this.trail.setColor(this.colorOf(this.lastAbility));
    this.trail.update(dt, has && melee && this.hero.busy && !this.dead, this.trailBase, this.trailTip);
  }

  /** Szarpnięcie kamery w kierunku (x, z) — przy ciężkim ciosie. */
  /**
   * Cios wroga dosięga bohatera: iskra po stronie wroga, czerwona liczba z obrażeniami zebranymi od poprzedniego ciosu
   * i szarpnięcie kamery od napastnika. Silnik liczy obrażenia od kontaktu w sposób ciągły — tu dostają one moment uderzenia.
   */
  private heroStruck(fromX: number, fromY: number): void {
    if (this.dead) return;
    const p = this.state.player;
    const dx = p.x - fromX;
    const dy = p.y - fromY;
    const d = Math.hypot(dx, dy) || 1;
    const at = this.world(p.x - (dx / d) * 10, p.y - (dy / d) * 10, 1.1);
    this.fx.impact(at, HURT_COLOR, false);
    if (this.pendingHurt >= 1) {
      this.numbers.show(this.world(p.x, p.y, 2.6), this.pendingHurt, false, HURT_COLOR);
      this.pendingHurt = 0;
    }
    this.hurtQuiet = 0;
    this.cameraKick(dx, dy, 0.12);
  }

  private cameraKick(dx: number, dz: number, amount: number): void {
    const len = Math.hypot(dx, dz) || 1;
    this.kick.x += (dx / len) * amount;
    this.kick.z += (dz / len) * amount;
  }

  /** Zatrzymanie klatki: krótkie, i nie częściej niż co ćwierć sekundy — inaczej gra by się „cięła”. */
  private addHitstop(seconds: number): void {
    if (this.time - this.lastHitstop < 0.25) return;
    this.lastHitstop = this.time;
    this.hitstop = Math.max(this.hitstop, seconds);
  }

  /**
   * Tempo czasu na tę klatkę: `sim` dla silnika, `visual` dla animacji i efektów.
   * Zatrzymanie po ciosie prawie mrozi akcję; po śmierci bohatera obraz zwalnia.
   */
  timeScales(realDt: number): { sim: number; visual: number } {
    if (this.hitstop > 0) {
      this.hitstop -= realDt;
      return { sim: 0.04, visual: 0.12 };
    }
    if (this.deathAt >= 0 && this.time - this.deathAt < 1.4) return { sim: 1, visual: 0.35 };
    if (this.deathSlowmo > 0) {
      this.deathSlowmo -= realDt;
      return { sim: 0.35, visual: 0.35 };
    }
    return { sim: 1, visual: 1 };
  }

  /** Ostatnio użyta umiejętność — kolor iskier przy trafieniu. */
  private lastAbility: string | null = null;

  /** Zapamiętuje pozycje przed krokiem symulacji (gracz pod kluczem -1). */
  snapshot(): void {
    const keep = (id: number, x: number, y: number) => {
      const p = this.prevPos.get(id);
      if (p) {
        p.x = x;
        p.y = y;
      } else this.prevPos.set(id, { x, y });
    };
    keep(-1, this.state.player.x, this.state.player.y);
    for (const e of this.state.enemies) keep(e.id, e.x, e.y);
    if (this.prevPos.size > this.state.enemies.length * 2 + 64) {
      const alive = new Set(this.state.enemies.map((e) => e.id));
      for (const id of this.prevPos.keys()) if (id !== -1 && !alive.has(id)) this.prevPos.delete(id);
    }
  }

  /** Ułamek kroku symulacji, który minął od ostatniego kroku (0..1). */
  setAlpha(alpha: number): void {
    this.alpha = Math.min(1, Math.max(0, alpha));
  }

  /** Pozycja na płaszczyźnie silnika wygładzona między krokami. */
  private smooth(id: number, x: number, y: number): { x: number; y: number } {
    const p = this.prevPos.get(id);
    if (!p || Math.hypot(x - p.x, y - p.y) > 200) return { x, y }; // teleport: bez rozmycia
    return { x: p.x + (x - p.x) * this.alpha, y: p.y + (y - p.y) * this.alpha };
  }

  update(dt: number): void {
    this.time += dt;
    this.frameDt = dt;
    this.syncHero(dt);
    this.syncEnemies(dt);
    // Obrażenia bez widocznego zamachu (pociski, kałuże, wróg bez animacji ciosu): liczba i tak się pojawia.
    if (this.pendingHurt > 0 && (this.hurtQuiet += dt) > 0.6) {
      const p = this.state.player;
      if (this.pendingHurt >= 1) this.numbers.show(this.world(p.x, p.y, 2.6), this.pendingHurt, false, HURT_COLOR);
      this.pendingHurt = 0;
      this.hurtQuiet = 0;
    }
    this.syncProjectiles();
    this.abilityViews.update(dt, this.hero.root);
    this.syncShots();
    this.syncGems();
    this.loot.update(dt);
    this.debris.update(dt);
    this.features.update(dt);
    this.bossView.update(dt);
    this.lights.update(dt, this.hero.root.position, this.env.lightSpots());
    this.updateTrail(dt);
    this.updateDying(dt);
    this.env.update(dt);
    this.fx.update(dt);
    this.sparks.update(dt);
    this.smoke.update(dt);
    this.numbers.update(dt);
    this.placeCamera(false);
  }

  private warm: EnemyView[] = [];

  /** Tworzy po jednym wrogu każdego rodzaju ze strefy (pod ziemią), żeby ich shadery skompilowały się z góry. */
  prewarm(): void {
    const zone = this.state.content.zones[this.state.zoneId];
    for (const z of [...(zone?.enemies ?? []), ...(zone?.bosses ?? [])]) {
      const def = this.state.content.enemies[z.enemy];
      if (!def) continue;
      const view = this.createEnemy(def.visual);
      view.model.root.position.set(this.hero.root.position.x, -50, this.hero.root.position.z);
      this.warm.push(view);
    }
    this.fx.flash(new Vector3(0, -50, 0), '#ffffff', 1, 0.01);
  }

  /** Po kompilacji rozgrzane modele trafiają do puli — pierwszy prawdziwy wróg użyje ich od razu. */
  releasePrewarm(): void {
    for (const view of this.warm) {
      view.model.root.removeFromParent();
      view.model.root.position.y = 0;
      const list = this.pool.get(view.visual) ?? [];
      list.push(view);
      this.pool.set(view.visual, list);
    }
    this.warm = [];
  }

  dispose(): void {
    for (const view of this.enemies.values()) view.model.dispose();
    for (const list of this.pool.values()) for (const v of list) v.model.dispose();
    this.hero.dispose();
    this.abilityViews.dispose();
    this.features.dispose();
    this.bossView.dispose();
  }

  private placeCamera(snap: boolean): void {
    const target = this.hero.root.position;
    // Im więcej wrogów wokół (albo boss), tym kamera lekko się oddala — widać zagrożenie.
    let near = 0;
    for (const e of this.state.enemies) if ((e.x * S - target.x) ** 2 + (e.y * S - target.z) ** 2 < 120) near++;
    const zoomTarget = 1 + Math.min(0.16, near * 0.006) + (this.state.bossIds.length > 0 ? 0.14 : 0);
    this.zoom += (zoomTarget - this.zoom) * damp(1.5, this.frameDt);
    const desired = target.clone().addScaledVector(CAMERA_OFFSET, this.zoom);
    // Kamera lekko wyprzedza kierunek biegu — widać, w co się wbiegnie. Po śmierci zbliża się do bohatera.
    desired.addScaledVector(this.heroVel, 0.35);
    if (this.deathAt >= 0) desired.copy(target).addScaledVector(CAMERA_OFFSET, 0.55);
    if (snap) this.camera.position.copy(desired);
    else this.camera.position.lerp(desired, damp(5, this.frameDt));
    const look = new Vector3(this.camera.position.x, 0.8, this.camera.position.z - CAMERA_OFFSET.z * this.zoom);
    // Szarpnięcie: kamera na ułamek sekundy podąża za kierunkiem ciosu i sprężyście wraca.
    this.kick.multiplyScalar(Math.exp(-10 * this.frameDt));
    // Wstrząsy i szarpnięcia kamery można wyłączyć w ustawieniach.
    if (!settings.shake) {
      this.kick.set(0, 0, 0);
      this.fx.shake = 0;
    }
    this.camera.position.add(this.kick);
    look.add(this.kick);
    if (this.fx.shake > 0) {
      const k = this.fx.shake * this.fx.shake * 1.6;
      this.camera.position.x += (Math.random() - 0.5) * k;
      this.camera.position.y += (Math.random() - 0.5) * k;
    }
    this.camera.lookAt(look);
    this.env.follow(target.x, target.z);
  }

  private syncHero(dt: number): void {
    const p = this.state.player;
    const pos = this.hero.root.position;
    this.lastHero.copy(pos);
    const sp = this.smooth(-1, p.x, p.y);
    pos.set(sp.x * S, 0, sp.y * S);
    const dx = pos.x - this.lastHero.x;
    const dz = pos.z - this.lastHero.z;
    const moving = !this.dead && Math.hypot(dx, dz) > 0.0005;
    if (dt > 0) this.heroVel.lerp(new Vector3(dx / dt, 0, dz / dt), damp(8, dt));

    if (!this.dead) {
      // Tempo kroków dopasowane do prędkości — stopy nie ślizgają się po ziemi.
      const speed = p.stats.moveSpeed * S;
      this.hero.setLoop(moving ? 'Running_A' : this.hero.idleLoop, moving ? Math.min(1.6, Math.max(0.7, speed / this.hero.runSpeed)) : 1);
      this.aimTimer -= dt;
      if (this.aimTimer > 0) {
        this.turnTowards(this.hero, this.aimAngle, 22, dt);
      } else if (moving) {
        this.turnTowards(this.hero, Math.atan2(dx, dz), 12, dt);
      }
      // Pochylenie w biegu i przechył na zakrętach — ruch ma wagę, a nie jest sztywny.
      const yaw = this.hero.root.rotation.y;
      let turn = yaw - this.lastYaw;
      turn = Math.atan2(Math.sin(turn), Math.cos(turn));
      this.lastYaw = yaw;
      const targetLean = moving ? 0.13 : 0;
      const targetBank = dt > 0 ? Math.max(-0.18, Math.min(0.18, (-turn / dt) * 0.03)) : 0;
      this.lean += (targetLean - this.lean) * damp(8, dt);
      this.bank += (targetBank - this.bank) * damp(10, dt);
      this.hero.body.rotation.set(this.lean, 0, this.bank);
      // Kurz spod stóp, naprzemiennie z lewej i prawej.
      if (moving) {
        this.stepTimer -= dt;
        if (this.stepTimer <= 0) {
          this.stepTimer = 0.3;
          this.stepSide = -this.stepSide;
          const yaw2 = this.hero.root.rotation.y;
          this.fx.footstep(new Vector3(pos.x + Math.cos(yaw2) * 0.2 * this.stepSide, 0.1, pos.z - Math.sin(yaw2) * 0.2 * this.stepSide), this.env.dust);
        }
      }
    }
    this.hero.update(dt);
  }

  private heroAttack(abilityId: string, tx: number, ty: number): void {
    if (this.dead) return;
    const def = this.state.content.abilities[abilityId];
    if (!def) return;
    const pos = this.hero.root.position;
    if (def.kind !== 'nova') {
      this.aimAngle = Math.atan2(tx * S - pos.x, ty * S - pos.z);
      this.aimTimer = AIM_HOLD;
    } else {
      // Atak obszarowy nie ma celu — bohater zamachuje się w stronę najbliższego wroga.
      const p = this.state.player;
      let best: { x: number; y: number } | null = null;
      let bestD = Infinity;
      for (const en of this.state.enemies) {
        const d = (en.x - p.x) ** 2 + (en.y - p.y) ** 2;
        if (d < bestD) {
          bestD = d;
          best = en;
        }
      }
      if (best) {
        this.aimAngle = Math.atan2(best.x - p.x, best.y - p.y);
        this.aimTimer = AIM_HOLD;
      }
    }
    this.altSwing = !this.altSwing;
    // Błysk przy dłoni przy wystrzale/rzucie czaru.
    if (def.kind === 'projectile') {
      const hand = pos.clone().add(new Vector3(Math.sin(this.aimAngle) * 0.6, 1.2, Math.cos(this.aimAngle) * 0.6));
      this.lights.flash(hand, this.colorOf(abilityId), def.fx === 'bolt' || def.fx === 'knife' ? 4 : 10, 6, 0.22);
      this.fx.flash(hand, this.colorOf(abilityId), def.fx === 'bolt' || def.fx === 'knife' ? 0.8 : 1.6, 0.14);
    }
    const anim = attackAnimation(def.fx, this.look.twoHanded, this.altSwing);
    if (!anim) return;
    // Obrót angażuje całe ciało (nogi też), reszta ataków tylko tułów — nogi dalej biegną.
    if (def.fx === 'spin') this.hero.playOnce(anim, { timeScale: 1.05, fade: 0.06 });
    // Cios bronią jednoręczną: lewa ręka (tarcza, sztylet) nie wykonuje zamachu oburęcznego klipu.
    else this.hero.playAttack(anim, def.kind === 'melee' ? 1.4 : 1.3, !this.look.twoHanded && def.kind === 'melee');
  }

  private turnTowards(model: CharacterModel, angle: number, rate: number, dt: number): void {
    const r = model.root.rotation;
    let d = angle - r.y;
    d = Math.atan2(Math.sin(d), Math.cos(d));
    r.y += d * damp(rate, dt);
  }

  private createEnemy(visual: string): EnemyView {
    const pooled = this.pool.get(visual)?.pop();
    if (pooled) {
      pooled.model.resetAnimations();
      pooled.swingCd = pooled.lunge = pooled.charging = pooled.strike = 0;
      this.scene.add(pooled.model.root);
      return pooled;
    }
    const v = ENEMY_VISUALS[visual] ?? ENEMY_VISUALS['skeleton-minion']!;
    const model = new CharacterModel(this.assets.template(v.model));
    if (v.weapon) model.attach('handslotr', this.assets.template(v.weapon).scene.clone());
    if (v.offhand) model.attach('handslotl', this.assets.template(v.offhand).scene.clone());
    model.root.scale.setScalar(v.scale);

    const materials: MeshStandardMaterial[] = [];
    model.root.traverse((o) => {
      const mesh = o as Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = true; // prawdziwy cień od słońca (plama pod stopami została tylko jako delikatny kontakt z ziemią)
      const mat = (mesh.material as MeshStandardMaterial).clone();
      addRim(mat);
      mesh.material = mat;
      materials.push(mat);
    });
    const blob = new Mesh(blobGeo, blobMat);
    blob.rotation.x = -Math.PI / 2;
    blob.position.y = 0.02;
    blob.scale.setScalar(0.45);
    model.root.add(blob);
    this.scene.add(model.root);
    return { visual, model, materials, flinch: 0, bar: null, tint: new Color(v.tint ?? '#000000'), swingCd: 0, lunge: 0, charging: 0, strike: 0 };
  }

  private syncEnemies(dt: number): void {
    const hero = this.hero.root.position;
    for (const e of this.state.enemies) {
      let view = this.enemies.get(e.id);
      if (!view) {
        const def = this.state.content.enemies[e.defId];
        view = this.createEnemy(def?.visual ?? 'skeleton-minion');
        view.model.setLoop(ENEMY_VISUALS[view.visual]?.walk ?? 'Walking_A', 0.6 + e.speed / 120);
        view.model.randomizePhase(e.id);
        // Szkielet wyłania się spod ziemi, zanim ruszy.
        if (view.model.has('Spawn_Ground_Skeletons')) view.model.playOnce('Spawn_Ground_Skeletons', { timeScale: 2.4 });
        view.flinch = 0;
        this.enemies.set(e.id, view);
      }
      const root = view.model.root;
      const ep = this.smooth(e.id, e.x, e.y);
      root.position.set(ep.x * S, 0, ep.y * S);
      // Wypad przy ciosie: wróg rzuca się kawałek do przodu i wraca.
      if (view.lunge > 0) {
        const yaw = root.rotation.y;
        root.position.x += Math.sin(yaw) * view.lunge * 0.35;
        root.position.z += Math.cos(yaw) * view.lunge * 0.35;
        view.lunge = Math.max(0, view.lunge - dt * 4);
      }
      // Szarża: bieg w przyspieszonym tempie.
      if (e.phase === 'charge') view.model.setLoop('Running_A', 2.2);
      else view.model.setLoop(ENEMY_VISUALS[view.visual]?.walk ?? 'Walking_A', 0.6 + e.speed / 120);
      this.turnTowards(view.model, Math.atan2(hero.x - root.position.x, hero.z - root.position.z), 7, dt);
      view.charging = Math.max(0, view.charging - dt);
      if (e.hitFlash > 0) this.tmpColor.setHex(0x772211);
      else this.tmpColor.copy(view.tint);
      // Statusy: żar przy podpaleniu, szron przy spowolnieniu, złoty błysk przy ogłuszeniu.
      if (e.burn) this.tmpColor.lerp(BURN_TINT, 0.5 + 0.25 * Math.sin(this.time * 18 + e.id));
      else if (e.stun) this.tmpColor.lerp(STUN_TINT, 0.55);
      else if (e.slow) this.tmpColor.lerp(SLOW_TINT, 0.6);
      if (view.charging > 0) this.tmpColor.lerp(PULSE, 0.35 + 0.35 * Math.sin(this.time * 28));
      for (const m of view.materials) m.emissive.copy(this.tmpColor);
      this.enemyMelee(view, e, dt);
      if (view.strike > 0) {
        view.strike -= dt;
        if (view.strike <= 0) this.heroStruck(e.x, e.y);
      }
      // Trafiony wróg odchyla się do tyłu i lekko spłaszcza, potem sprężyście wraca.
      view.flinch *= Math.exp(-9 * dt);
      const base = ENEMY_VISUALS[view.visual]?.scale ?? 1;
      view.model.body.rotation.x = -0.45 * view.flinch;
      root.scale.set(base * (1 + 0.08 * view.flinch), base * (1 - 0.12 * view.flinch), base * (1 + 0.08 * view.flinch));
      if (!this.state.bossIds.includes(e.id)) this.syncBar(view, e.hp, e.maxHp, e.radius >= 20);
      view.model.update(dt);
    }
  }

  /**
   * Cios wręcz: wróg, który dosięga bohatera, zamachuje się bronią (z wypadem do przodu),
   * zamiast stać przy nim bez ruchu. Tylko wygląd — obrażenia liczy silnik.
   */
  private enemyMelee(view: EnemyView, e: Enemy, dt: number): void {
    view.swingCd -= dt;
    if (view.swingCd > 0 || e.phase === 'windup' || e.phase === 'charge') return;
    const p = this.state.player;
    const reach = this.state.content.game.playerRadius + e.radius + 14;
    if ((e.x - p.x) ** 2 + (e.y - p.y) ** 2 > reach * reach) return;
    const v = ENEMY_VISUALS[view.visual];
    if (!v || !view.model.has(v.melee)) return;
    view.model.playOnce(v.melee, { timeScale: 1.3, fade: 0.08 });
    view.lunge = 1;
    view.strike = 0.22;
    // Bohater, który właśnie dostaje, widzi częstsze ciosy — każdy z nich pokazuje zadane obrażenia.
    view.swingCd = (this.pendingHurt > 0 ? 0.7 : 1) + ((e.id * 37) % 10) / 20;
  }

  /** Zapowiedź ataku: animacja zamachu, a dla szarży i salwy znak na ziemi. */
  private enemyWindup(id: number, x: number, y: number, angle: number, duration: number): void {
    const view = this.enemies.get(id);
    const e = this.state.enemies.find((en) => en.id === id);
    const def = e && this.state.content.enemies[e.defId];
    if (!view || !e || !def) return;
    const v = ENEMY_VISUALS[view.visual];
    const cast = v?.cast;
    if (cast && view.model.has(cast)) {
      const len = view.model.clipDuration(cast);
      view.model.playOnce(cast, { timeScale: len > 0 ? Math.max(0.6, Math.min(2.5, len / (duration + 0.25))) : 1, fade: 0.08 });
    }
    view.swingCd = duration + 0.5;
    view.charging = duration;
    const atk = def.attack;
    if (!atk) return;
    if (def.behavior === 'charger') this.fx.telegraphLine(this.world(x, y, 0), angle, (atk.range / atk.speed + 0.15) * atk.speed * S, e.radius * 2 * S, duration);
    else if (def.behavior === 'exploder') this.fx.telegraphRing(view.model.root, atk.radius * S, duration, '#ff5a20');
    else if (def.behavior === 'boss') this.fx.telegraphRing(view.model.root, e.radius * 3 * S, duration, def.color);
    else this.fx.flash(this.world(x + Math.cos(angle) * e.radius, y + Math.sin(angle) * e.radius, 1.3), def.color, 0.9, duration);
  }

  /** Pasek życia nad głową: zawsze u elity (Kolos), u zwykłych wrogów dopiero po pierwszym trafieniu. */
  private syncBar(view: EnemyView, hp: number, maxHp: number, elite: boolean): void {
    const show = elite || hp < maxHp;
    if (!show) {
      if (view.bar) {
        view.bar.bg.visible = false;
        view.bar.fill.visible = false;
      }
      return;
    }
    if (!view.bar) {
      const bg = new Sprite(barBgMat);
      const fill = new Sprite(elite ? barEliteMat : barFillMat);
      fill.center.set(0, 0.5);
      bg.renderOrder = fill.renderOrder = 9;
      this.scene.add(bg, fill);
      view.bar = { bg, fill };
    }
    const scale = ENEMY_VISUALS[view.visual]?.scale ?? 1;
    const w = elite ? 1.6 : 1.0;
    const p = view.model.root.position;
    const y = 2.55 * scale;
    view.bar.bg.visible = view.bar.fill.visible = true;
    view.bar.bg.position.set(p.x, y, p.z);
    view.bar.bg.scale.set(w + 0.08, 0.16, 1);
    view.bar.fill.position.set(p.x - w / 2, y, p.z);
    view.bar.fill.scale.set(Math.max(0.001, w * Math.max(0, hp / maxHp)), 0.1, 1);
  }

  private killEnemy(id: number): void {
    const view = this.enemies.get(id);
    if (!view) return;
    this.enemies.delete(id);
    const boss = view.visual.startsWith('boss-');
    const scale = ENEMY_VISUALS[view.visual]?.scale ?? 1;
    const at = view.model.root.position.clone();
    const away = at.clone().sub(this.hero.root.position).setY(0).normalize();
    this.debris.burst(at, away, scale, scale > 1.3);
    // Ciężki wróg pada z krótkim zatrzymaniem klatki — czuć wagę ciosu.
    if (!boss && scale > 1.3) this.addHitstop(0.06);
    if (!boss) {
      // Zwykły szkielet rozsypuje się od razu na kości (zamiast długiej animacji upadku).
      for (const m of view.materials) m.emissive.setHex(0x000000);
      if (view.bar) view.bar.bg.visible = view.bar.fill.visible = false;
      view.model.root.scale.setScalar(scale);
      view.model.body.rotation.x = 0;
      this.dying.push({ view, t: DEATH_DURATION });
      return;
    }
    for (const m of view.materials) m.emissive.setHex(0x000000);
    if (view.bar) {
      view.bar.bg.visible = false;
      view.bar.fill.visible = false;
    }
    view.model.root.scale.setScalar(ENEMY_VISUALS[view.visual]?.scale ?? 1);
    view.model.body.rotation.x = 0;
    const anim = view.model.has('Death_C_Skeletons') ? 'Death_C_Skeletons' : 'Death_A';
    view.model.playOnce(anim, { hold: true, timeScale: 1.4 });
    this.dying.push({ view, t: 0 });
  }

  private updateDying(dt: number): void {
    for (const d of this.dying) {
      d.t += dt;
      d.view.model.update(dt);
      if (d.t > DEATH_DURATION * 0.6) d.view.model.root.position.y = -((d.t - DEATH_DURATION * 0.6) / (DEATH_DURATION * 0.4)) * 0.8;
    }
    const done = this.dying.filter((d) => d.t >= DEATH_DURATION);
    this.dying = this.dying.filter((d) => d.t < DEATH_DURATION);
    for (const { view } of done) {
      view.model.root.removeFromParent();
      view.model.root.position.y = 0;
      const list = this.pool.get(view.visual) ?? [];
      list.push(view);
      this.pool.set(view.visual, list);
    }
  }

  private syncProjectiles(): void {
    const seen = new Set<number>();
    for (const p of this.state.projectiles) {
      seen.add(p.id);
      let slot = this.projectiles.get(p.id);
      if (!slot) {
        const def = this.state.content.abilities[p.abilityId];
        const fx = def?.fx ?? 'orb';
        const view = createProjectile(fx, this.colorOf(p.abilityId), Math.max(0.8, p.radius / 6));
        this.scene.add(view.root);
        slot = { view, fx };
        this.projectiles.set(p.id, slot);
      }
      const view = slot.view;
      const root = view.root;
      root.position.set(p.x * S, PROJECTILE_HEIGHT, p.y * S);
      root.rotation.y = -Math.atan2(p.vy, p.vx);
      if (view.spinner) view.spinner.rotation.y = this.time * 24;
      for (const g of view.pulse) g.material.opacity = 0.7 + 0.3 * Math.sin(this.time * 30 + p.id);
      if (view.trail) {
        this.sparks.emit(root.position, { color: view.trail.color, count: 2, speed: 0.4, life: 0.32, size: view.trail.size, grow: 0.2, hot: view.trail.hot, drag: 3 });
      }
    }
    for (const [id, slot] of this.projectiles) {
      if (seen.has(id)) continue;
      slot.view.root.removeFromParent();
      this.projectiles.delete(id);
    }
  }

  /** Pociski wrogów: ten sam wygląd co pociski bohatera (kula, bełt, odłamek), w kolorze wroga. */
  private syncShots(): void {
    const seen = new Set<number>();
    for (const sh of this.state.hostileShots) {
      seen.add(sh.id);
      let view = this.shots.get(sh.id);
      if (!view) {
        const def = this.state.content.enemies[sh.defId];
        view = createProjectile(def?.attack?.fx ?? 'orb', def?.color ?? '#b58cff', Math.max(0.8, sh.radius / 6));
        this.scene.add(view.root);
        this.shots.set(sh.id, view);
      }
      view.root.position.set(sh.x * S, PROJECTILE_HEIGHT, sh.y * S);
      view.root.rotation.y = Math.atan2(-sh.vy, sh.vx);
      for (const p of view.pulse) p.scale.setScalar(p.scale.x * 0.9 + (1 + Math.sin(this.time * 18) * 0.12) * 0.1 * p.scale.x);
      if (view.trail && Math.random() < 0.5) this.sparks.emit(view.root.position, { color: view.trail.color, count: 1, speed: 0.3, life: 0.3, size: view.trail.size, hot: view.trail.hot, drag: 3 });
    }
    for (const [id, view] of this.shots) {
      if (seen.has(id)) continue;
      view.root.removeFromParent();
      this.shots.delete(id);
    }
  }

  private syncGems(): void {
    const seen = new Set<number>();
    for (const g of this.state.gems) {
      seen.add(g.id);
      let mesh = this.gems.get(g.id);
      if (!mesh) {
        mesh = new Group();
        const crystal = new Mesh(gemGeo, gemMat);
        const halo = new Sprite(gemGlowMat);
        halo.scale.setScalar(0.9);
        mesh.add(crystal, halo);
        mesh.scale.setScalar(g.value >= 5 ? 1.6 : 1);
        this.scene.add(mesh);
        this.gems.set(g.id, mesh);
      }
      // Kryształ wyskakuje łukiem z pokonanego wroga (silnik rozsuwa go na boki), odbija się i unosi nad ziemią.
      if (!this.gemBorn.has(g.id)) this.gemBorn.set(g.id, this.time);
      const age = this.time - this.gemBorn.get(g.id)!;
      const pop = lootHop(age, 1.1);
      mesh.position.set(g.x * S, 0.45 + pop + Math.sin(this.time * 4 + g.id) * 0.1, g.y * S);
      mesh.children[0]!.scale.setScalar(Math.min(1, 0.3 + age * 3));
      mesh.children[0]!.rotation.y = this.time * 2 + g.id;
    }
    for (const [id, mesh] of this.gems) {
      if (seen.has(id)) continue;
      // Zebrany kryształ: błysk i dźwięk.
      sfx.pickup();
      this.sparks.emit(mesh.position, { color: '#7ee8ff', count: 6, speed: 1.8, life: 0.35, up: 1.2, size: 0.16, hot: true });
      mesh.removeFromParent();
      this.gems.delete(id);
      this.gemBorn.delete(id);
    }
  }
}
